// File: frontend/src/components/admin/AdminDashboard.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUsers, FaFileAlt, FaFutbol, FaCircle, FaFlag,
  FaBuilding, FaShieldAlt, FaUser,
  FaTrash, FaPen, FaHashtag, FaPalette,
  FaCheckCircle, FaPauseCircle, FaBan, FaSnowflake,
  FaUserClock, FaUserTimes, FaMoon,
} from "react-icons/fa";
import {
  fetchDashboardStats,
  fetchDashboardKpi,
  fetchDashboardChart,
  DashboardStats,
  DashboardKpi,
  DashboardChartPoint,
  DashboardMetric,
  DashboardKpiPeriod,
  DashboardChartPeriod,
  DashboardChartType,
  DashboardThemeCount,
  DashboardUserStatusCount,
} from "../../services/adminDashboardService";
import { getOnlineUsers } from "../../services/onlineUsersService";
import { onOnlinePresenceChanged } from "../../services/websocketService";
import { formatCompactNumber, formatCompactWithTitle, formatFullNumber } from "../../utils";
import { formatRefreshTime } from "../../utils/adminUtils";
import { AdminRefreshButton } from "./common";
import { DashboardChartSection } from "./DashboardChartSection";
import { DashboardRecentSection } from "./DashboardRecentSection";
import "./AdminDashboard.css";

// ── Shared number renderer ─────────────────────────────────────────────────

interface DashNumberProps {
  value: number | undefined;
  className?: string;
}

/**
 * Renders a KPI number in shortened form ("1,2k", "3,4M") once it reaches
 * 1000, so it keeps fitting inside the small circle badges as the platform
 * grows. When the value was shortened, the exact number is attached as a
 * native tooltip; below 1000 nothing is shortened and no tooltip is added.
 */
const DashNumber: React.FC<DashNumberProps> = ({ value, className }) => {
  const { text, title } = formatCompactWithTitle(value);
  return <span className={className} title={title}>{text}</span>;
};

// ── KPI Card ───────────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | undefined;
  iconClass?: string;
  isLive?: boolean;
  // Optional click handler — renders the card as a button-like clickable element.
  onClick?: () => void;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, iconClass, isLive, onClick }) => (
  <div
    className={`dash-kpi-card${onClick ? " dash-kpi-card--clickable" : ""}`}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
  >
    <div className="dash-kpi-card-top">
      <span className={`dash-kpi-icon${iconClass ? ` ${iconClass}` : ""}`}>{icon}</span>
      {isLive && <span className="dash-kpi-live-badge">live</span>}
    </div>
    <span className="dash-kpi-label">{label}</span>
    <DashNumber className="dash-kpi-value" value={value} />
  </div>
);

// ── User breakdown card — shows total + ROOKIE/AUTHOR/MODERATOR/ADMIN split ─

// Same declaration order as backend UserStatus.java, so the circles always
// line up the same way regardless of the order the API happens to return them.
const USER_STATUS_ORDER = [
  "ACTIVE", "SUSPENDED", "BANNED", "FROZEN_BY_USER",
  "DELETED_BY_USER", "DELETED_BY_ADMIN", "INACTIVE",
];

// Icon shown inside each account-status circle. Mirrors the marker icons used
// in userStatusIconMap.ts so a status always reads the same across the app.
const USER_STATUS_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  ACTIVE:           FaCheckCircle,
  SUSPENDED:        FaPauseCircle,
  BANNED:           FaBan,
  FROZEN_BY_USER:   FaSnowflake,
  DELETED_BY_USER:  FaUserClock,
  DELETED_BY_ADMIN: FaUserTimes,
  INACTIVE:         FaMoon,
};

// i18n key per status — short labels, they have to fit inside a small circle.
const USER_STATUS_I18N_KEYS: Record<string, string> = {
  ACTIVE:           "dash_kpi_user_status_active",
  SUSPENDED:        "dash_kpi_user_status_suspended",
  BANNED:           "dash_kpi_user_status_banned",
  FROZEN_BY_USER:   "dash_kpi_user_status_frozen",
  DELETED_BY_USER:  "dash_kpi_user_status_deleted_by_user",
  DELETED_BY_ADMIN: "dash_kpi_user_status_deleted_by_admin",
  INACTIVE:         "dash_kpi_user_status_inactive",
};

// Highlight colour variant applied to a status circle when its count is > 0.
// Reuses the same variant classes as the entry action circles, plus two new
// neutral/info variants defined in AdminDashboard.css.
const USER_STATUS_VARIANTS: Record<string, string> = {
  ACTIVE:           "dash-kpi-entry-row--active",
  SUSPENDED:        "dash-kpi-entry-row--warning",
  BANNED:           "dash-kpi-entry-row--danger",
  FROZEN_BY_USER:   "dash-kpi-entry-row--info",
  DELETED_BY_USER:  "dash-kpi-entry-row--warning-dark",
  DELETED_BY_ADMIN: "dash-kpi-entry-row--danger-dark",
  INACTIVE:         "dash-kpi-entry-row--neutral",
};

interface UserBreakdownCardProps {
  total: number | undefined;
  rookies: number | undefined;
  authors: number | undefined;
  moderators: number | undefined;
  admins: number | undefined;
  labelTotal: string;
  labelRookie: string;
  labelAuthor: string;
  labelModerator: string;
  labelAdmin: string;
  // Account-status breakdown (banned / suspended / deleted / …) — one entry per
  // UserStatus enum value, rendered as a second circle row under the roles.
  userStatusCounts: DashboardUserStatusCount[] | undefined;
}

const UserBreakdownCard: React.FC<UserBreakdownCardProps> = ({
  total, rookies, authors, moderators, admins,
  labelTotal, labelRookie, labelAuthor, labelModerator, labelAdmin,
  userStatusCounts,
}) => {
  const { t } = useTranslation();

  const countByStatus = new Map(
    (userStatusCounts ?? []).map((sc) => [sc.status, sc.userCount])
  );

  const renderStatusCircle = (statusKey: string) => {
    const count   = countByStatus.get(statusKey);
    const Icon    = USER_STATUS_ICONS[statusKey];
    const label   = t(USER_STATUS_I18N_KEYS[statusKey] ?? statusKey);
    const variant = (count ?? 0) > 0 ? ` ${USER_STATUS_VARIANTS[statusKey]}` : "";
    // Icon only, no text: 7 circles on one row leaves no room for labels
    // (they truncated / wrapped). The name is exposed via the tooltip instead.
    return (
      <div
        key={statusKey}
        className={`dash-kpi-entry-row${variant}`}
        title={`${label}: ${formatFullNumber(count)}`}
      >
        <span className="dash-kpi-entry-row-label dash-kpi-entry-row-label--icon-only">
          {Icon && <Icon size={14} />}
        </span>
        {/* Plain span (not DashNumber): the circle already carries a tooltip
            with the status name and the exact count, and a nested title would
            shadow it while hovering the number. */}
        <span className="dash-kpi-entry-row-value">{formatCompactNumber(count)}</span>
      </div>
    );
  };

  return (
    <div className="dash-kpi-card dash-kpi-card--users">

      {/* Top half: icon left, total centered */}
      <div className="dash-kpi-entries-top">
        <span className="dash-kpi-icon"><FaUsers /></span>
        <div className="dash-kpi-entries-total-block">
          <span className="dash-kpi-label">{labelTotal}</span>
          <DashNumber className="dash-kpi-value" value={total} />
        </div>
      </div>

      {/* First breakdown row: by role */}
      <div className="dash-kpi-entries-breakdown">
        <div className={`dash-kpi-entry-row${(rookies ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaUser size={10} /> {labelRookie}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={rookies} />
        </div>
        <div className={`dash-kpi-entry-row${(authors ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaFileAlt size={10} /> {labelAuthor}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={authors} />
        </div>
        <div className={`dash-kpi-entry-row${(moderators ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaShieldAlt size={10} /> {labelModerator}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={moderators} />
        </div>
        <div className={`dash-kpi-entry-row${(admins ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaUsers size={10} /> {labelAdmin}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={admins} />
        </div>
      </div>

      {/* Second breakdown row: by account lifecycle status — 7 smaller circles */}
      <div className="dash-kpi-entries-breakdown dash-kpi-entries-breakdown--statuses">
        {USER_STATUS_ORDER.map(renderStatusCircle)}
      </div>

    </div>
  );
};

// ── Entry breakdown card — shows total + match/person/club/team split ──────

interface EntryBreakdownCardProps {
  total: number | undefined;
  matchEntries: number | undefined;
  personEntries: number | undefined;
  clubEntries: number | undefined;
  teamEntries: number | undefined;
  customTopicEntries: number | undefined;
  labelTotal: string;
  labelMatch: string;
  labelPerson: string;
  labelClub: string;
  labelTeam: string;
  labelCustomTopic: string;
  // Entry action counts (all-time, unaffected by period)
  userDeleted: number | undefined;
  userEdited: number | undefined;
  modAdminDeleted: number | undefined;
  modAdminEdited: number | undefined;
  labelUserDeleted: string;
  labelUserEdited: string;
  labelModDeleted: string;
  labelModEdited: string;
}

const EntryBreakdownCard: React.FC<EntryBreakdownCardProps> = ({
  total, matchEntries, personEntries, clubEntries, teamEntries, customTopicEntries,
  labelTotal, labelMatch, labelPerson, labelClub, labelTeam, labelCustomTopic,
  userDeleted, userEdited, modAdminDeleted, modAdminEdited,
  labelUserDeleted, labelUserEdited, labelModDeleted, labelModEdited,
}) => {
  return (
    <div className="dash-kpi-card dash-kpi-card--entries">

      {/* Top half: icon left, total centered */}
      <div className="dash-kpi-entries-top">
        <span className="dash-kpi-icon"><FaFileAlt /></span>
        <div className="dash-kpi-entries-total-block">
          <span className="dash-kpi-label">{labelTotal}</span>
          <DashNumber className="dash-kpi-value" value={total} />
        </div>
      </div>

      {/* First breakdown row: by subject type (Gündem / Maç / Kulüp / Kişi / Takım) */}
      <div className="dash-kpi-entries-breakdown">
        <div className={`dash-kpi-entry-row${(customTopicEntries ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaHashtag size={10} /> {labelCustomTopic}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={customTopicEntries} />
        </div>
        <div className={`dash-kpi-entry-row${(matchEntries ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaFutbol size={10} /> {labelMatch}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={matchEntries} />
        </div>
        <div className={`dash-kpi-entry-row${(clubEntries ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaBuilding size={10} /> {labelClub}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={clubEntries} />
        </div>
        <div className={`dash-kpi-entry-row${(personEntries ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaUser size={10} /> {labelPerson}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={personEntries} />
        </div>
        <div className={`dash-kpi-entry-row${(teamEntries ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaShieldAlt size={10} /> {labelTeam}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={teamEntries} />
        </div>
      </div>

      {/* Second breakdown row: by action type — user deletes/edits and mod/admin deletes/edits */}
      <div className="dash-kpi-entries-breakdown dash-kpi-entries-breakdown--actions">
        <div className={`dash-kpi-entry-row dash-kpi-entry-row--user-del${(userDeleted ?? 0) > 0 ? " dash-kpi-entry-row--danger" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaTrash size={10} /> {labelUserDeleted}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={userDeleted} />
        </div>
        <div className={`dash-kpi-entry-row dash-kpi-entry-row--user-edit${(userEdited ?? 0) > 0 ? " dash-kpi-entry-row--warning" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaPen size={10} /> {labelUserEdited}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={userEdited} />
        </div>
        <div className={`dash-kpi-entry-row dash-kpi-entry-row--mod-del${(modAdminDeleted ?? 0) > 0 ? " dash-kpi-entry-row--danger-dark" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaTrash size={10} /> {labelModDeleted}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={modAdminDeleted} />
        </div>
        <div className={`dash-kpi-entry-row dash-kpi-entry-row--mod-edit${(modAdminEdited ?? 0) > 0 ? " dash-kpi-entry-row--warning-dark" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaPen size={10} /> {labelModEdited}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={modAdminEdited} />
        </div>
      </div>

    </div>
  );
};

// ── Entity breakdown card — same layout as EntryBreakdownCard ─────────────
// Top: icon + "Toplam Varlıklar" title + sum centered
// Bottom: Maç / Kişi / Kulüp / Takım in one row (labels without "Toplam")

interface EntityBreakdownCardProps {
  matches: number | undefined;
  persons: number | undefined;
  clubs: number | undefined;
  teams: number | undefined;
  topics: number | undefined;
  labelTotal: string;
  labelMatch: string;
  labelPerson: string;
  labelClub: string;
  labelTeam: string;
  labelCustomTopic: string;
}

const EntityBreakdownCard: React.FC<EntityBreakdownCardProps> = ({
  matches, persons, clubs, teams, topics,
  labelTotal, labelMatch, labelPerson, labelClub, labelTeam, labelCustomTopic,
}) => {
  const total = (matches ?? 0) + (persons ?? 0) + (clubs ?? 0) + (teams ?? 0) + (topics ?? 0);

  return (
    <div className="dash-kpi-card dash-kpi-card--entities">

      {/* Top half: icon left, total centered */}
      <div className="dash-kpi-entries-top">
        <span className="dash-kpi-icon"><FaShieldAlt /></span>
        <div className="dash-kpi-entries-total-block">
          <span className="dash-kpi-label">{labelTotal}</span>
          <DashNumber className="dash-kpi-value" value={total} />
        </div>
      </div>

      {/* Bottom half: 5 items in a row (Gündem / Maç / Kulüp / Kişi / Takım) */}
      <div className="dash-kpi-entries-breakdown">
        <div className={`dash-kpi-entry-row${(topics ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaHashtag size={10} /> {labelCustomTopic}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={topics} />
        </div>
        <div className={`dash-kpi-entry-row${(matches ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaFutbol size={10} /> {labelMatch}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={matches} />
        </div>
        <div className={`dash-kpi-entry-row${(clubs ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaBuilding size={10} /> {labelClub}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={clubs} />
        </div>
        <div className={`dash-kpi-entry-row${(persons ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaUser size={10} /> {labelPerson}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={persons} />
        </div>
        <div className={`dash-kpi-entry-row${(teams ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}>
          <span className="dash-kpi-entry-row-label"><FaShieldAlt size={10} /> {labelTeam}</span>
          <DashNumber className="dash-kpi-entry-row-value" value={teams} />
        </div>
      </div>

    </div>
  );
};

// ── Theme breakdown card — 10 themes ──────────────────────────────────────

// Same enum-declaration order as backend Theme.java, so circles always line
// up the same way regardless of what order the API happens to return them in.
const THEME_ORDER = [
  "LIGHT", "DARK", "PAPER", "TANGO", "FLAMINGO", "COMOLOKKO", "LOKUM", "MERCAN", "GOKYUZU", "TARAFTAR",
];

// Reuses the exact i18n keys already shown in the theme picker (SettingsPage.tsx)
// so a theme's name always reads the same everywhere in the app.
const THEME_I18N_KEYS: Record<string, string> = {
  LIGHT: "theme_light",
  DARK: "theme_dark",
  PAPER: "theme_paper",
  TANGO: "theme_tango",
  FLAMINGO: "theme_flamingo",
  COMOLOKKO: "theme_comolokko",
  LOKUM: "theme_lokum",
  MERCAN: "theme_mercan",
  GOKYUZU: "theme_gokyuzu",
  TARAFTAR: "theme_taraftar",
};

interface ThemeBreakdownCardProps {
  themeCounts: DashboardThemeCount[] | undefined;
  labelTotal: string;
}

const ThemeBreakdownCard: React.FC<ThemeBreakdownCardProps> = ({
  themeCounts, labelTotal,
}) => {
  const { t } = useTranslation();
  const countByTheme = new Map((themeCounts ?? []).map((tc) => [tc.theme, tc.userCount]));
  const total = themeCounts ? themeCounts.reduce((sum, tc) => sum + tc.userCount, 0) : undefined;

  const renderCircle = (themeKey: string) => {
    const count = countByTheme.get(themeKey);
    return (
      <div
        key={themeKey}
        className={`dash-kpi-entry-row${(count ?? 0) > 0 ? " dash-kpi-entry-row--active" : ""}`}
      >
        <span className="dash-kpi-entry-row-label">{t(THEME_I18N_KEYS[themeKey] ?? themeKey)}</span>
        <DashNumber className="dash-kpi-entry-row-value" value={count} />
      </div>
    );
  };

  return (
    <div className="dash-kpi-card dash-kpi-card--themes">

      {/* Top half: icon left, total centered */}
      <div className="dash-kpi-entries-top">
        <span className="dash-kpi-icon"><FaPalette /></span>
        <div className="dash-kpi-entries-total-block">
          <span className="dash-kpi-label">{labelTotal}</span>
          <DashNumber className="dash-kpi-value" value={total} />
        </div>
      </div>

      {/* 10 theme circles across two rows — the reserved slot that used to sit
          here is now taken by the taraftar (fan) theme */}
      <div className="dash-kpi-entries-breakdown">
        {THEME_ORDER.slice(0, 5).map(renderCircle)}
      </div>
      <div className="dash-kpi-entries-breakdown">
        {THEME_ORDER.slice(5, 10).map(renderCircle)}
      </div>

    </div>
  );
};

// ── KPI period tooltip helper ──────────────────────────────────────────────

/** Returns a human-readable date-range string for a given KPI period. */
function kpiPeriodTooltip(period: DashboardKpiPeriod): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (period) {
    case "today": {
      return fmt(today);
    }
    case "week": {
      const from = new Date(today);
      from.setDate(today.getDate() - 6);
      return `${fmt(from)} – ${fmt(today)}`;
    }
    case "month": {
      const from = new Date(today);
      from.setDate(today.getDate() - 29);
      return `${fmt(from)} – ${fmt(today)}`;
    }
    case "thisyear": {
      const from = new Date(today.getFullYear(), 0, 1);
      return `${fmt(from)} – ${fmt(today)}`;
    }
    case "total":
    default:
      return "Tüm veriler";
  }
}

// ── Main component ─────────────────────────────────────────────────────────

interface AdminDashboardProps {
  // Called when the "Bekleyen Başlık" (pending topic suggestions) KPI card is
  // clicked — lets the parent (AdminPage) switch to the custom-topics admin tab.
  onNavigateToCustomTopics?: () => void;
  // Called when the "Bekleyen Şikayet" (pending reports) KPI card is clicked —
  // lets the parent (AdminPage) switch to the reports admin tab.
  onNavigateToReports?: () => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigateToCustomTopics, onNavigateToReports }) => {
  const { t } = useTranslation();

  const [stats, setStats]               = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError]     = useState(false);

  // KPI period default: "today" (Bugün = second visible button but logically first)
  const [kpi, setKpi]               = useState<DashboardKpi | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [kpiPeriod, setKpiPeriod]   = useState<DashboardKpiPeriod>("today");

  // Chart defaults: weekly bar chart
  const [chartData, setChartData]       = useState<DashboardChartPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError]     = useState(false);
  const [metric, setMetric]             = useState<DashboardMetric>("entries");
  const [chartPeriod, setChartPeriod]   = useState<DashboardChartPeriod>("week");
  const [chartType, setChartType]       = useState<DashboardChartType>("bar");

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(false);
    try   { setStats(await fetchDashboardStats()); }
    catch { setStatsError(true); }
    finally { setStatsLoading(false); }
  }, []);

  // Timestamp of the last data load, shown next to the refresh button the same
  // way the user / author / topic panels do it.
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  const loadKpi = useCallback(async () => {
    setKpiLoading(true);
    try   { setKpi(await fetchDashboardKpi(kpiPeriod)); }
    catch { setKpi(null); }
    finally {
      setKpiLoading(false);
      // Stamped here rather than in handleRefreshAll so the label is present on
      // the very first load, the same way the other admin panels behave.
      setLastRefreshedAt(new Date());
    }
  }, [kpiPeriod]);

  const loadChart = useCallback(async () => {
    setChartLoading(true);
    setChartError(false);
    try   { setChartData(await fetchDashboardChart(metric, chartPeriod)); }
    catch { setChartData([]); setChartError(true); }
    finally { setChartLoading(false); }
  }, [metric, chartPeriod]);

  // "Şu an Online" live value.
  //
  // Bug: the card is marked "live" but its value came from the KPI response,
  // which is only loaded on mount, on period change and on "Yenile". Anyone who
  // connected after that was missing, so the card could show 1 while the online
  // modal already listed 2. It now reads the same endpoint as the online modal
  // (so both always show the same number) and re-reads it whenever the server
  // signals an online/offline transition. null = not loaded yet, falls back to
  // the KPI value.
  const [liveOnlineCount, setLiveOnlineCount] = useState<number | null>(null);

  const loadOnlineCount = useCallback(async () => {
    try   { setLiveOnlineCount((await getOnlineUsers()).length); }
    catch { /* keep the previous value; the KPI value is the fallback */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadKpi();   }, [loadKpi]);
  useEffect(() => { loadChart(); }, [loadChart]);
  useEffect(() => { loadOnlineCount(); }, [loadOnlineCount]);

  // Debounced so a burst of connects (e.g. after a deploy) causes one request
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onOnlinePresenceChanged(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        loadOnlineCount();
      }, 1_000);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [loadOnlineCount]);

  // Refreshes all three data sources at once
  const handleRefreshAll = useCallback(() => {
    loadStats();
    loadKpi();
    loadChart();
    loadOnlineCount();
  }, [loadStats, loadKpi, loadChart, loadOnlineCount]);

  if (statsLoading && kpiLoading) {
    return <div className="dash-loading">{t("dash_loading")}</div>;
  }
  if (statsError) {
    return (
      <div className="dash-error">
        <span>{t("dash_error")}</span>
        <button className="dash-retry-btn" onClick={loadStats}>{t("dash_retry")}</button>
      </div>
    );
  }

  const kpiPeriods: { key: DashboardKpiPeriod; label: string }[] = [
    { key: "today",    label: t("dash_kpi_period_today")   },
    { key: "week",     label: t("dash_kpi_period_week")    },
    { key: "month",    label: t("dash_kpi_period_month")   },
    { key: "thisyear", label: t("dash_period_thisyear")    },
    { key: "total",    label: t("dash_kpi_period_total")   },
  ];

  return (
    <div className="admin-dashboard">

      {/* KPI period selector: Bugün | Bu Hafta | Bu Ay | Tüm Zamanlar */}
      <div className="dash-kpi-header">
        <div className="dash-kpi-period-toggle">
          {kpiPeriods.map((p) => (
            <button
              key={p.key}
              className={`dash-kpi-period-btn${kpiPeriod === p.key ? " active" : ""}`}
              onClick={() => setKpiPeriod(p.key)}
              title={kpiPeriodTooltip(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {kpiLoading && <span className="dash-kpi-refreshing">{t("dash_loading")}</span>}
        {lastRefreshedAt && (
          <span className="dash-last-refreshed">
            {t("admin_last_refreshed", { time: formatRefreshTime(lastRefreshedAt) })}
          </span>
        )}
        {/* Refresh button — reloads stats, KPI cards and chart simultaneously */}
        <AdminRefreshButton
          onClick={handleRefreshAll}
          refreshing={statsLoading || kpiLoading || chartLoading}
          label={t("dash_refresh", "Yenile")}
        />
      </div>

      {/* KPI Cards — live values on top, breakdown cards below */}

      {/* Live row: Şu an Online / Bekleyen Şikayet / Bekleyen Başlık — always up to date, unaffected by the period selector */}
      <div className="dash-kpi-live-row">
        <KpiCard icon={<FaCircle />} label={t("dash_kpi_online")} value={liveOnlineCount ?? kpi?.onlineUsers} iconClass="success" isLive />
        <KpiCard
          icon={<FaFlag />}
          label={t("dash_kpi_pending_reports")}
          value={kpi?.pendingReports}
          iconClass={kpi?.pendingReports ? "danger" : undefined}
          isLive
          onClick={onNavigateToReports}
        />
        {/* Bekleyen Başlık — pending custom topic suggestions, links to the admin custom topics tab */}
        <KpiCard
          icon={<FaHashtag />}
          label={t("dash_kpi_pending_topics")}
          value={kpi?.pendingTopicSuggestions}
          iconClass={kpi?.pendingTopicSuggestions ? "danger" : undefined}
          isLive
          onClick={onNavigateToCustomTopics}
        />
      </div>

      {/* Breakdown row: Toplam Kullanıcı / Toplam Tanım / Toplam Başlıklar */}
      <div className="dash-kpi-row">

        {/* User breakdown card — shows total + per-role split */}
        <UserBreakdownCard
          total={kpi?.users}
          rookies={kpi?.rookieUsers}
          authors={kpi?.authorUsers}
          moderators={kpi?.moderatorUsers}
          admins={kpi?.adminUsers}
          labelTotal={t("dash_kpi_total_users")}
          labelRookie={t("dash_kpi_user_rookie")}
          labelAuthor={t("dash_kpi_user_author")}
          labelModerator={t("dash_kpi_user_moderator")}
          labelAdmin={t("dash_kpi_user_admin")}
          userStatusCounts={kpi?.userStatusCounts}
        />

        {/* Entry breakdown card — shows total + per-type split */}
        <EntryBreakdownCard
          total={kpi?.entries}
          matchEntries={kpi?.matchEntries}
          personEntries={kpi?.personEntries}
          clubEntries={kpi?.clubEntries}
          teamEntries={kpi?.teamEntries}
          customTopicEntries={kpi?.customTopicEntries}
          labelTotal={t("dash_kpi_total_entries")}
          labelMatch={t("dash_kpi_entry_match")}
          labelPerson={t("dash_kpi_entry_person")}
          labelClub={t("dash_kpi_entry_club")}
          labelTeam={t("dash_kpi_entry_team")}
          labelCustomTopic={t("dash_kpi_entry_custom_topic")}
          userDeleted={kpi?.userDeletedEntries}
          userEdited={kpi?.userEditedEntries}
          modAdminDeleted={kpi?.modAdminDeletedEntries}
          modAdminEdited={kpi?.modAdminEditedEntries}
          labelUserDeleted={t("dash_kpi_entry_user_deleted")}
          labelUserEdited={t("dash_kpi_entry_user_edited")}
          labelModDeleted={t("dash_kpi_entry_mod_deleted")}
          labelModEdited={t("dash_kpi_entry_mod_edited")}
        />

        {/* Entity breakdown card — Toplam Başlıklar: Gündem / Maç / Kulüp / Kişi / Takım */}
        <EntityBreakdownCard
          matches={kpi?.matches}
          persons={kpi?.persons}
          clubs={kpi?.clubs}
          teams={kpi?.teams}
          topics={kpi?.topics}
          labelTotal={t("dash_kpi_total_entities")}
          labelMatch={t("dash_kpi_entity_match")}
          labelPerson={t("dash_kpi_entity_person")}
          labelClub={t("dash_kpi_entity_club")}
          labelTeam={t("dash_kpi_entity_team")}
          labelCustomTopic={t("dash_kpi_entity_custom_topic")}
        />

        {/* Theme breakdown card — same row as the 3 cards above; the 3-column
            grid auto-wraps it to a new line in the leftmost column, so it's
            same size, no separate wrapper needed. */}
        <ThemeBreakdownCard
          themeCounts={kpi?.themeCounts}
          labelTotal={t("dash_kpi_total_themes")}
        />
      </div>

      <DashboardChartSection
        chartData={chartData}
        loading={chartLoading}
        error={chartError}
        metric={metric}
        setMetric={setMetric}
        chartPeriod={chartPeriod}
        setChartPeriod={setChartPeriod}
        chartType={chartType}
        setChartType={setChartType}
        onRetry={loadChart}
      />

      <DashboardRecentSection stats={stats} />
    </div>
  );
};